import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { CurrentUser } from 'src/api/endpoints/auth/api';
import { Branch } from 'src/api/endpoints/branches/api';
import AuthPage from 'src/pages/AuthPage';
import { FC, ReactElement } from 'react';
import StudentTimetablePage from 'src/pages/student/TimetablePage';

import {
    AuthorizedUserProps,
    ProtectedRoute,
} from 'src/routes/components/PrivateRoute';
import { AnonymousRoute } from 'src/routes/components/AnonymousRoute';
import { RoleGroups, UserRole } from 'src/api/endpoints/users/users.types.new';
import IndexPage from 'src/pages/student/IndexPage';
import { TimetablePage } from 'src/pages/Timetable';
import { AttendantPage } from 'src/pages/Attendant';
import { MaterialsPage } from 'src/pages/Materials';
import { EduSubjects } from 'src/pages';
import { NotificationsPage } from 'src/pages/Notifications';
import { GroupsPage } from 'src/pages/edu/Groups';
import { GroupPage } from 'src/pages/edu/GroupPage';
import { StudentsPage } from 'src/pages/edu/Students';
import { StudentPage } from 'src/pages/edu/Statistic';
import { AudiencesPage } from 'src/pages/edu/Audience';
import { AcademicVacations } from 'src/pages/edu/AcademicVacations';
import { AttendanceStatistic } from 'src/pages/edu/AttendanceStatistic';
import { Users } from 'src/pages/Users';
import { ActiveLessonsPage } from 'src/pages/branch-teachers/ActiveLessonsPage/ActiveLessonsPage';
import { AdminHomeworkPage } from 'src/pages/branch-teachers/HomeworksPage';
import ProgressPage from 'src/pages/student/ProgressPage';
import StudentMaterialsPage from 'src/pages/student/MaterialPage';
import HomeworkPage from 'src/pages/student/HomeworkPage';
import ProfilePage from 'src/pages/student/ProfilePage';
import { IntroducingPage } from 'src/pages/student/introducing';
import { AttendanceHistoryPage } from 'src/pages/AttendanceHistory';
import { Loader } from 'src/components/shared/loader';
import { BranchDebtsPage } from 'src/pages/employees/BranchStudentTotalDebtsPage';
import { BranchStudentDebtsPage } from 'src/pages/employees/BranchStudentDebtsPage';
import { DebtPage } from 'src/pages/student/DebtPage';

export type StudentInformation =
    | {
          loading: true;
          registered: boolean | undefined | null;
          hasDebts: boolean | undefined | null;
      }
    | {
          loading: false;
          registered: boolean;
          hasDebts: boolean;
      };

export type RoutingProps = {
    loading?: boolean;
    user?: CurrentUser | null;
    branches?: Branch[];
    student?: StudentInformation | null;
};

const RoleIndexPage: FC<AuthorizedUserProps> = (props: AuthorizedUserProps) => {
    const { currentBranchId, role } = props;

    let roleIndexPagePath: string;

    switch (role) {
        case UserRole.BranchStudent:
            return <IndexPage {...props} />;

        case UserRole.BranchTeacher:
            roleIndexPagePath = `/b/${currentBranchId}/attendance`;
            break;

        case UserRole.BranchEducationDepartment:
            roleIndexPagePath = `/b/${currentBranchId}/schedule`;
            break;

        case UserRole.BranchDirector:
            roleIndexPagePath = `/b/${currentBranchId}/schedule`;
            break;

        case UserRole.OrganizationDirector:
            roleIndexPagePath = `/employees`;
    }

    return <Navigate to={roleIndexPagePath} replace={true} />;
};

export const Routing: FC<RoutingProps> = (
    props: RoutingProps
): ReactElement => {
    const { user, branches, student } = props;

    if (student) {
        if (student.loading) return <Loader />;

        if (!student.registered) {
            return (
                <BrowserRouter>
                    <Routes>
                        <Route
                            path={'/introducing'}
                            element={
                                <ProtectedRoute
                                    user={user}
                                    userBranches={branches}
                                    element={p => {
                                        return <IntroducingPage {...p} />;
                                    }}
                                />
                            }
                        />

                        <Route
                            path={'*'}
                            element={<Navigate to={'/introducing'} />}
                        />
                    </Routes>
                </BrowserRouter>
            );
        }

        if (student.hasDebts) {
            return (
                <BrowserRouter>
                    <Routes>
                        <Route
                            path={'/debts'}
                            element={
                                <ProtectedRoute
                                    user={user}
                                    userBranches={branches}
                                    expectedRoles={[UserRole.BranchStudent]}
                                    element={p => {
                                        return <DebtPage {...p} />;
                                    }}
                                />
                            }
                        />

                        <Route
                            path={'*'}
                            element={<Navigate to={'/debts'} />}
                        />
                    </Routes>
                </BrowserRouter>
            );
        }
    }

    return (
        <BrowserRouter>
            <Routes>
                <Route
                    path={'/login'}
                    element={
                        <AnonymousRoute user={user} element={<AuthPage />} />
                    }
                />

                <Route
                    path={'/'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            element={RoleIndexPage}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/debts'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={RoleGroups.Administration}
                            element={p => <BranchDebtsPage {...p} />}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/students/:studentId/debts'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={RoleGroups.Administration}
                            element={p => <BranchStudentDebtsPage {...p} />}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/schedule'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={RoleGroups.Employees}
                            element={p => {
                                const isTeacher: boolean =
                                    p.role === UserRole.BranchTeacher;

                                return (
                                    <TimetablePage
                                        allowEdit={!isTeacher}
                                        {...p}
                                    />
                                );
                            }}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/attendance'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={RoleGroups.Employees}
                            element={p => {
                                if (p.role === UserRole.BranchTeacher)
                                    return <ActiveLessonsPage {...p} />;
                                return <AttendantPage {...p} />;
                            }}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/attendance/history'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={[
                                UserRole.BranchEducationDepartment,
                                UserRole.OrganizationDirector,
                                UserRole.BranchDirector,
                            ]}
                            element={p => <AttendanceHistoryPage {...p} />}
                        />
                    }
                />

                <Route
                    path={'/materials/'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            element={p => {
                                if (p.role === UserRole.BranchStudent) {
                                    return <StudentMaterialsPage {...p} />;
                                }

                                const isTeacher: boolean =
                                    p.role === UserRole.BranchTeacher;

                                return (
                                    <MaterialsPage
                                        allowEdit={!isTeacher}
                                        {...p}
                                    />
                                );
                            }}
                        />
                    }
                />

                <Route
                    path={'/subjects'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={[
                                UserRole.BranchEducationDepartment,
                                UserRole.OrganizationDirector,
                                UserRole.BranchDirector,
                            ]}
                            element={p => {
                                return <EduSubjects {...p} />;
                            }}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/audiences'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={[
                                UserRole.BranchEducationDepartment,
                                UserRole.OrganizationDirector,
                                UserRole.BranchDirector,
                            ]}
                            element={p => {
                                return <AudiencesPage {...p} />;
                            }}
                        />
                    }
                />

                <Route
                    path={'/notifications'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            element={p => {
                                return <NotificationsPage {...p} />;
                            }}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/groups'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={RoleGroups.Employees}
                            element={p => {
                                return <GroupsPage {...p} />;
                            }}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/groups/:groupId'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={RoleGroups.Employees}
                            element={p => {
                                return <GroupPage {...p} />;
                            }}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/students'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={RoleGroups.Employees}
                            element={p => (
                                <StudentsPage
                                    {...p}
                                    features={{
                                        search: true,
                                        filter: {
                                            registerStatus: true,
                                            group: true,
                                            academicStatus: true,
                                        },
                                    }}
                                />
                            )}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/students/academic-leaves'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={[
                                UserRole.BranchEducationDepartment,
                                UserRole.OrganizationDirector,
                                UserRole.BranchDirector,
                            ]}
                            element={p => <AcademicVacations {...p} />}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/students/:studentId'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={RoleGroups.Employees}
                            element={p => <StudentPage {...p} />}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/students/:studentId/attendance'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={RoleGroups.Employees}
                            element={p => <AttendanceStatistic {...p} />}
                        />
                    }
                />

                <Route
                    path={'/employees'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={[UserRole.OrganizationDirector]}
                            element={p => (
                                <Users {...p} directorCompany={true} />
                            )}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/employees'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={[UserRole.BranchDirector]}
                            element={p => <Users {...p} />}
                        />
                    }
                />

                <Route
                    path={'/b/:branchId/homeworks'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={[UserRole.BranchTeacher]}
                            element={p => <AdminHomeworkPage {...p} />}
                        />
                    }
                />

                <Route
                    path={'/progress'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={[UserRole.BranchStudent]}
                            element={p => <ProgressPage {...p} />}
                        />
                    }
                />

                <Route
                    path={'/homeworks'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={[UserRole.BranchStudent]}
                            element={p => <HomeworkPage {...p} />}
                        />
                    }
                />

                <Route
                    path={'/schedule'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            expectedRoles={[UserRole.BranchStudent]}
                            element={p => <StudentTimetablePage {...p} />}
                        />
                    }
                />

                <Route
                    path={'/profile'}
                    element={
                        <ProtectedRoute
                            user={user}
                            userBranches={branches}
                            element={p => <ProfilePage {...p} />}
                        />
                    }
                />

                <Route path={'*'} element={<Navigate to={'/'} />} />
            </Routes>
        </BrowserRouter>
    );
};
